(() => {
  if (customElements.get('jrb-carousel-gallery')) {
    return;
  }

  class JRBCarouselGallery extends HTMLElement {
    constructor() {
      super();
      this.activeIndex = 0;
      this.timer = null;
      this.editorPaused = false;
      this.isPointerDown = false;
      this.pointerStartX = 0;
      this.pointerDeltaX = 0;
      this.boundResize = this.handleResize.bind(this);
      this.boundPointerMove = this.handlePointerMove.bind(this);
      this.boundPointerUp = this.handlePointerUp.bind(this);
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') {
        return;
      }

      this.dataset.initialized = 'true';
      this.viewport = this.querySelector('[data-carousel-viewport]');
      this.stage = this.querySelector('[data-carousel-stage]');
      this.status = this.querySelector('[data-carousel-status]');
      this.prevButton = this.querySelector('[data-carousel-prev]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
      this.captionPanels = Array.from(this.querySelectorAll('[data-carousel-caption]'));
      this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileQuery = window.matchMedia('(max-width: 767px)');
      this.slideCount = this.slides.length;
      this.loop = this.dataset.loop === 'true';
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplayInterval = parseInt(this.dataset.autoplayInterval, 10) || 5000;
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';
      this.enableSwipe = this.dataset.enableDragSwipe === 'true';
      this.enableKeyboardNavigation = this.dataset.enableKeyboardNavigation === 'true';
      this.clickBehavior = this.dataset.clickBehavior || 'none';

      if (!this.viewport || !this.stage || this.slideCount === 0) {
        return;
      }

      this.onPrevClick = () => this.goTo(this.activeIndex - 1, true);
      this.onNextClick = () => this.goTo(this.activeIndex + 1, true);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onViewportClick = this.handleViewportClick.bind(this);
      this.onDotClick = this.handleDotClick.bind(this);
      this.onMouseEnter = () => this.pauseAutoplay();
      this.onMouseLeave = () => this.resumeAutoplay();
      this.onFocusIn = () => this.pauseAutoplay();
      this.onFocusOut = (event) => {
        if (!this.contains(event.relatedTarget)) {
          this.resumeAutoplay();
        }
      };
      this.onBlockSelect = this.handleBlockSelect.bind(this);
      this.onBlockDeselect = this.handleBlockDeselect.bind(this);
      this.onPointerDown = this.handlePointerDown.bind(this);

      this.addEventListener('click', this.onViewportClick);
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      this.addEventListener('shopify:block:select', this.onBlockSelect);
      this.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      window.addEventListener('resize', this.boundResize);

      if (this.prevButton) {
        this.prevButton.addEventListener('click', this.onPrevClick);
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', this.onNextClick);
      }

      this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));

      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', this.onMouseEnter);
        this.addEventListener('mouseleave', this.onMouseLeave);
      }

      if (this.enableSwipe) {
        this.viewport.addEventListener('pointerdown', this.onPointerDown);
      }

      this.update(true);
      this.resumeAutoplay();
    }

    disconnectedCallback() {
      this.pauseAutoplay();

      if (this.prevButton) {
        this.prevButton.removeEventListener('click', this.onPrevClick);
      }

      if (this.nextButton) {
        this.nextButton.removeEventListener('click', this.onNextClick);
      }

      this.dots.forEach((dot) => dot.removeEventListener('click', this.onDotClick));
      this.removeEventListener('click', this.onViewportClick);
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.removeEventListener('shopify:block:deselect', this.onBlockDeselect);

      if (this.pauseOnHover) {
        this.removeEventListener('mouseenter', this.onMouseEnter);
        this.removeEventListener('mouseleave', this.onMouseLeave);
      }

      if (this.enableSwipe && this.viewport) {
        this.viewport.removeEventListener('pointerdown', this.onPointerDown);
      }

      window.removeEventListener('resize', this.boundResize);
      window.removeEventListener('pointermove', this.boundPointerMove);
      window.removeEventListener('pointerup', this.boundPointerUp);
      window.removeEventListener('pointercancel', this.boundPointerUp);
      this.dataset.initialized = 'false';
    }

    handleResize() {
      this.update(true);
    }

    handleViewportClick(event) {
      if (
        event.target.closest('[data-carousel-dot]') ||
        event.target.closest('[data-carousel-prev]') ||
        event.target.closest('[data-carousel-next]') ||
        event.target.closest('.jrb-carousel-gallery__cta')
      ) {
        return;
      }

      const trigger = event.target.closest('[data-carousel-trigger]');
      if (!trigger) {
        return;
      }

      const slide = trigger.closest('[data-carousel-slide]');
      const index = this.slides.indexOf(slide);

      if (index === -1) {
        return;
      }

      event.preventDefault();

      if (index !== this.activeIndex) {
        this.goTo(index, true);
        return;
      }

      if (this.clickBehavior === 'advance') {
        this.goTo(this.activeIndex + 1, true);
        return;
      }

      if (this.clickBehavior === 'open_link') {
        const url = slide.dataset.slideUrl;
        if (url) {
          if (slide.dataset.slideOpenNewTab === 'true') {
            window.open(url, '_blank', 'noopener');
          } else {
            window.location.assign(url);
          }
        }
      }
    }

    handleDotClick(event) {
      const dot = event.currentTarget;
      const index = parseInt(dot.dataset.dotIndex, 10);
      if (Number.isNaN(index)) {
        return;
      }

      this.goTo(index, true);
    }

    handleKeydown(event) {
      if (!this.enableKeyboardNavigation || this.slideCount <= 1) {
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.activeIndex - 1, true);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.activeIndex + 1, true);
      }
    }

    handlePointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) {
        return;
      }

      const ignoreTarget = event.target.closest('[data-carousel-prev], [data-carousel-next], [data-carousel-dot], .jrb-carousel-gallery__cta');
      if (ignoreTarget) {
        return;
      }

      this.isPointerDown = true;
      this.pointerStartX = event.clientX;
      this.pointerDeltaX = 0;
      window.addEventListener('pointermove', this.boundPointerMove);
      window.addEventListener('pointerup', this.boundPointerUp);
      window.addEventListener('pointercancel', this.boundPointerUp);
    }

    handlePointerMove(event) {
      if (!this.isPointerDown) {
        return;
      }

      this.pointerDeltaX = event.clientX - this.pointerStartX;
    }

    handlePointerUp() {
      if (!this.isPointerDown) {
        return;
      }

      const delta = this.pointerDeltaX;
      this.isPointerDown = false;
      this.pointerDeltaX = 0;
      window.removeEventListener('pointermove', this.boundPointerMove);
      window.removeEventListener('pointerup', this.boundPointerUp);
      window.removeEventListener('pointercancel', this.boundPointerUp);

      if (Math.abs(delta) < 42) {
        return;
      }

      if (delta < 0) {
        this.goTo(this.activeIndex + 1, true);
      } else {
        this.goTo(this.activeIndex - 1, true);
      }
    }

    handleBlockSelect(event) {
      const block = event.target.closest('[data-carousel-slide]');
      if (!block) {
        return;
      }

      const index = this.slides.indexOf(block);
      if (index === -1) {
        return;
      }

      this.editorPaused = true;
      this.pauseAutoplay();
      this.goTo(index, false);
    }

    handleBlockDeselect() {
      this.editorPaused = false;
      this.resumeAutoplay();
    }

    goTo(index, userInitiated) {
      if (this.slideCount <= 1) {
        return;
      }

      const nextIndex = this.normalizeIndex(index);

      if (nextIndex === this.activeIndex) {
        return;
      }

      this.activeIndex = nextIndex;
      this.update(false);

      if (userInitiated) {
        this.pauseAutoplay();
        this.resumeAutoplay();
      }
    }

    normalizeIndex(index) {
      if (this.loop && this.slideCount > 2) {
        return (index + this.slideCount) % this.slideCount;
      }

      if (index < 0) {
        return 0;
      }

      if (index > this.slideCount - 1) {
        return this.slideCount - 1;
      }

      return index;
    }

    pauseAutoplay() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    resumeAutoplay() {
      if (!this.autoplayEnabled || this.reducedMotionQuery.matches || this.slideCount <= 1 || this.editorPaused) {
        return;
      }

      this.pauseAutoplay();
      this.timer = window.setInterval(() => {
        this.goTo(this.activeIndex + 1, false);
      }, this.autoplayInterval);
    }

    getPeekCount() {
      const value = this.mobileQuery.matches ? this.dataset.peekMobile : this.dataset.peekDesktop;
      const parsed = parseInt(value, 10);
      return Number.isNaN(parsed) ? 1 : parsed;
    }

    getGap() {
      const value = this.mobileQuery.matches ? this.dataset.gapMobile : this.dataset.gapDesktop;
      const parsed = parseInt(value, 10);
      return Number.isNaN(parsed) ? 24 : parsed;
    }

    getSlideWidthRatio() {
      const peek = this.getPeekCount();

      if (this.mobileQuery.matches) {
        return [0.92, 0.84, 0.76][peek] || 0.84;
      }

      return [0.8, 0.72, 0.62, 0.56][peek] || 0.72;
    }

    getRelativeIndex(index) {
      if (!this.loop || this.slideCount <= 2) {
        return index - this.activeIndex;
      }

      let delta = index - this.activeIndex;
      const half = this.slideCount / 2;

      if (delta > half) {
        delta -= this.slideCount;
      } else if (delta < -half) {
        delta += this.slideCount;
      }

      return delta;
    }

    computeTranslate(distance, slideWidth, gap) {
      let translate = 0;

      for (let step = 1; step <= distance; step += 1) {
        translate += (slideWidth * (step === 1 ? 0.56 : 0.34)) + gap;
      }

      return translate;
    }

    update(immediate) {
      if (!this.viewport) {
        return;
      }

      const peek = this.getPeekCount();
      const slideWidthRatio = this.getSlideWidthRatio();
      const gap = this.getGap();
      const slideWidth = this.viewport.clientWidth * slideWidthRatio;
      const sideOpacity = parseFloat(this.dataset.sideOpacity || '0.56');
      const scaleStep = parseFloat(this.dataset.scaleStep || '0.1');

      this.style.setProperty('--jrbcg-slide-current-width', `${slideWidthRatio * 100}%`);

      this.slides.forEach((slide, index) => {
        const relative = this.getRelativeIndex(index);
        const distance = Math.abs(relative);
        const isActive = distance === 0;
        const isVisible = distance <= peek || isActive;
        const direction = relative < 0 ? -1 : 1;
        const translate = direction * this.computeTranslate(distance, slideWidth, gap);
        const scale = isActive ? 1 : Math.max(0.56, 1 - (scaleStep * distance));
        const opacity = isActive ? 1 : Math.max(0, sideOpacity - ((distance - 1) * 0.16));
        const zIndex = 100 - distance;
        const blur = isActive ? 0 : Math.min(2.2, distance * 0.55);
        const trigger = slide.querySelector('[data-carousel-trigger]');

        slide.classList.toggle('is-active', isActive);
        slide.classList.toggle('is-prev', relative === -1);
        slide.classList.toggle('is-next', relative === 1);
        slide.classList.toggle('is-far-prev', relative < -1 && isVisible);
        slide.classList.toggle('is-far-next', relative > 1 && isVisible);
        slide.classList.toggle('is-visible', isVisible);
        slide.classList.toggle('is-outside', !isVisible);
        slide.style.setProperty('--jrbcg-translate', `${translate}px`);
        slide.style.setProperty('--jrbcg-scale', scale.toFixed(3));
        slide.style.setProperty('--jrbcg-opacity', isVisible ? opacity.toFixed(3) : '0');
        slide.style.setProperty('--jrbcg-z', String(zIndex));
        slide.style.setProperty('--jrbcg-blur', `${isVisible ? blur : 0}px`);
        slide.setAttribute('aria-hidden', isVisible ? 'false' : 'true');

        if (trigger) {
          trigger.tabIndex = isVisible ? 0 : -1;
        }
      });

      this.dots.forEach((dot, index) => {
        const isActive = index === this.activeIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      this.captionPanels.forEach((panel, index) => {
        const isActive = index === this.activeIndex;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });

      if (this.prevButton) {
        this.prevButton.disabled = !this.loop && this.activeIndex === 0;
      }

      if (this.nextButton) {
        this.nextButton.disabled = !this.loop && this.activeIndex === this.slideCount - 1;
      }

      if (this.status) {
        const activeSlide = this.slides[this.activeIndex];
        const title = activeSlide ? activeSlide.dataset.slideTitle : '';
        this.status.textContent = `Showing slide ${this.activeIndex + 1} of ${this.slideCount}${title ? `: ${title}` : ''}.`;
      }

      if (immediate) {
        this.classList.add('is-ready');
      }
    }
  }

  customElements.define('jrb-carousel-gallery', JRBCarouselGallery);
})();
